import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toastSomethingError } from '@/components/ui/toast-content';
import { PATH_PAGE } from '@/router/path';
import { RouteParams } from '@/router/type';
import { createProject } from '@/service/serverless-gpu';
import {
  useGetServerlessInstanceDefault,
  useGetServerlessInstancePricing,
} from '@/service/serverless-gpu/hook';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ExternalLink, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

const ServerlessGPUCreateProject = () => {
  const [instanceType, setInstanceType] = useState<string>('');
  const [projectName, setProjectName] = useState<string>('');
  const navigate = useNavigate();
  const { workspaceId } = useParams<RouteParams>();

  const { data: instanceTypeData } = useGetServerlessInstanceDefault();
  const { data: instanceTypePricingData } = useGetServerlessInstancePricing();

  useEffect(() => {
    if (instanceTypeData) {
      setInstanceType(instanceTypeData.instance_type);
    }
  }, [instanceTypeData]);

  const { mutate, isPending } = useMutation({
    mutationFn: createProject,
    onSuccess: (data) => {
      navigate(
        PATH_PAGE.dashboard.serverlessGPU.byId(workspaceId!, data.project_id)
          .overview
      );
      toast.success('Project created successfully');
    },
    onError: (error) => {
      toastSomethingError(error);
    },
  });

  const onSubmit = () => {
    mutate({
      instance_type: instanceType,
      project_name: projectName,
    });
  };

  const selectedInstance = instanceTypePricingData?.find(
    (instance) => instance.instance_type === instanceType
  );

  return (
    <div className="container mx-auto mt-8 max-w-2xl rounded-lg border p-6">
      <div className="space-y-6">
        <div>
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-900">
              Create New Project
            </h1>
          </div>
          <p className="mt-2 text-gray-600">
            Select a GPU instance type for your project. Each instance type
            comes with different specifications and pricing.
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              GPU Instance Type
            </label>
            <Select onValueChange={setInstanceType} value={instanceType}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select an instance type" />
              </SelectTrigger>

              <SelectContent>
                {instanceTypePricingData?.map((option) => (
                  <SelectItem
                    key={option.instance_type}
                    value={option.instance_type}
                  >
                    {`${option.instance_type}`}
                    <span className="ml-2 text-xs capitalize text-gray-500">
                      {option.instance_region.charAt(0).toUpperCase() +
                        option.instance_region.slice(1)}{' '}
                      |{' '}
                      {option.instance_provider.charAt(0).toUpperCase() +
                        option.instance_provider.slice(1)}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedInstance && (
            <div className="rounded-md bg-gray-50 p-4">
              <div className="mb-2 flex items-center justify-between">
                <h3 className="font-medium text-gray-900">Instance Details</h3>
                <Link
                  to={PATH_PAGE.dashboard.serverlessGPU.pricing(workspaceId!)}
                  className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View Pricing Details
                  <ExternalLink className="h-3 w-3" />
                </Link>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-600">Type</p>
                  <p className="font-medium">
                    {selectedInstance.instance_type}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">Price</p>
                  <p className="font-medium">
                    ${selectedInstance.price_per_hour / 3600}/second
                  </p>
                </div>
              </div>
            </div>
          )}

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              Project Name{' '}
              <span className="text-xs text-gray-500">(Optional)</span>
            </label>
            <Input
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              placeholder="Enter project name"
            />
          </div>

          <div className="pt-4">
            <Button
              type="submit"
              disabled={isPending || !instanceType}
              onClick={onSubmit}
              variant="primary"
              className="w-full"
              startIcon={
                isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null
              }
            >
              Create Project
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServerlessGPUCreateProject;
